import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
} from '@mui/material';
import { LandingPageSettings } from '../../services/landingPageApi';

interface AppearanceTabProps {
  data: LandingPageSettings;
  onSave: (updates: Partial<LandingPageSettings>) => void;
  isSaving: boolean;
}

// Predefined color options
const COLOR_PRESETS = [
  { name: 'Indigo', value: '#5D5FEF' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Rose', value: '#E11D48' },
  { name: 'Amber', value: '#D97706' },
  { name: 'Emerald', value: '#059669' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Purple', value: '#7C3AED' },
  { name: 'Pink', value: '#DB2777' },
];

export const AppearanceTab = ({ data, onSave, isSaving }: AppearanceTabProps) => {
  const [primaryColor, setPrimaryColor] = useState('#5D5FEF');

  useEffect(() => {
    setPrimaryColor(data.primaryColor || '#5D5FEF');
  }, [data]);

  const handleColorChange = (color: string) => {
    setPrimaryColor(color);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ primaryColor });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Brand Color
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a primary color for your landing page. This will be used for buttons, accents, and highlights.
      </Typography>

      {/* Color Presets */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {COLOR_PRESETS.map((preset) => (
          <Grid item key={preset.value}>
            <Box
              onClick={() => handleColorChange(preset.value)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: preset.value,
                cursor: 'pointer',
                border: primaryColor === preset.value ? '3px solid' : '3px solid transparent',
                borderColor: primaryColor === preset.value ? 'grey.900' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
              title={preset.name}
            />
          </Grid>
        ))}
      </Grid>

      {/* Custom Color Picker */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Or choose a custom color:
        </Typography>
        <Box
          component="input"
          type="color"
          value={primaryColor}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange(e.target.value)}
          sx={{
            width: 48,
            height: 48,
            border: 'none',
            borderRadius: 1,
            cursor: 'pointer',
            padding: 0,
            '&::-webkit-color-swatch-wrapper': {
              padding: 0,
            },
            '&::-webkit-color-swatch': {
              border: 'none',
              borderRadius: 1,
            },
          }}
        />
        <TextField
          value={primaryColor}
          onChange={(e) => {
            if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) {
              handleColorChange(e.target.value);
            }
          }}
          size="small"
          sx={{ width: 120 }}
          placeholder="#5D5FEF"
        />
      </Box>

      {/* Preview */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Preview
      </Typography>

      <Box
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: primaryColor,
              '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
            }}
          >
            Primary Button
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': { borderColor: primaryColor, bgcolor: `${primaryColor}10` },
            }}
          >
            Outlined Button
          </Button>
        </Box>

        <Typography variant="body1" sx={{ color: primaryColor, fontWeight: 600 }}>
          This is how your accent color will appear
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          sx={{ px: 4 }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
